import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { FloppyDisk, X } from '@phosphor-icons/react';

function ImmobileFields({ formData, handleChange }) {
    return (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                    <Label>Indirizzo</Label>
                    <Input value={formData.indirizzo || ''} onChange={e => handleChange('indirizzo', e.target.value)} placeholder="Via Roma" />
                </div>
                <div>
                    <Label>Civico</Label>
                    <Input value={formData.civico || ''} onChange={e => handleChange('civico', e.target.value)} placeholder="123" />
                </div>
                <div>
                    <Label>CAP</Label>
                    <Input value={formData.cap || ''} onChange={e => handleChange('cap', e.target.value)} placeholder="00100" />
                </div>
                <div>
                    <Label>Comune</Label>
                    <Input data-testid="immobile-comune-input" value={formData.comune || ''} onChange={e => handleChange('comune', e.target.value)} placeholder="Roma" />
                </div>
                <div>
                    <Label>Provincia</Label>
                    <Input value={formData.provincia || ''} onChange={e => handleChange('provincia', e.target.value)} placeholder="RM" maxLength={2} />
                </div>
            </div>

            <div>
                <Label>Tipologia Immobile</Label>
                <Select value={formData.tipologia || ''} onValueChange={v => handleChange('tipologia', v)}>
                    <SelectTrigger><SelectValue placeholder="Seleziona tipologia" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="residenziale">Residenziale</SelectItem>
                        <SelectItem value="commerciale">Commerciale</SelectItem>
                        <SelectItem value="industriale">Industriale</SelectItem>
                        <SelectItem value="agricolo">Agricolo</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Dati Catastali</p>
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <Label>Foglio</Label>
                        <Input value={formData.foglio || ''} onChange={e => handleChange('foglio', e.target.value)} />
                    </div>
                    <div>
                        <Label>Particella</Label>
                        <Input value={formData.particella || ''} onChange={e => handleChange('particella', e.target.value)} />
                    </div>
                    <div>
                        <Label>Subalterno</Label>
                        <Input value={formData.subalterno || ''} onChange={e => handleChange('subalterno', e.target.value)} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Latitudine</Label>
                    <Input type="number" step="0.000001" value={formData.latitudine || ''} onChange={e => handleChange('latitudine', e.target.value === '' ? null : parseFloat(e.target.value))} />
                </div>
                <div>
                    <Label>Longitudine</Label>
                    <Input type="number" step="0.000001" value={formData.longitudine || ''} onChange={e => handleChange('longitudine', e.target.value === '' ? null : parseFloat(e.target.value))} />
                </div>
            </div>

            <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Utenza Elettrica</p>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>POD</Label>
                        <Input value={formData.pod || ''} onChange={e => handleChange('pod', e.target.value)} placeholder="IT001E..." />
                    </div>
                    <div>
                        <Label>Potenza Contatore (kW)</Label>
                        <Input type="number" step="0.1" value={formData.potenza_contatore_kw || ''} onChange={e => handleChange('potenza_contatore_kw', e.target.value === '' ? null : parseFloat(e.target.value))} />
                    </div>
                </div>
            </div>

            <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                    <input type="checkbox" id="prop_diverso" checked={formData.proprietario_diverso || false} onChange={e => handleChange('proprietario_diverso', e.target.checked)} className="rounded" />
                    <Label htmlFor="prop_diverso" className="cursor-pointer">Proprietario diverso dal cliente</Label>
                </div>
                {formData.proprietario_diverso && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                        <div>
                            <Label>Nome</Label>
                            <Input value={formData.proprietario_nome || ''} onChange={e => handleChange('proprietario_nome', e.target.value)} />
                        </div>
                        <div>
                            <Label>Cognome</Label>
                            <Input value={formData.proprietario_cognome || ''} onChange={e => handleChange('proprietario_cognome', e.target.value)} />
                        </div>
                        <div>
                            <Label>Codice Fiscale</Label>
                            <Input value={formData.proprietario_codice_fiscale || ''} onChange={e => handleChange('proprietario_codice_fiscale', e.target.value)} />
                        </div>
                        <div>
                            <Label>Telefono</Label>
                            <Input value={formData.proprietario_telefono || ''} onChange={e => handleChange('proprietario_telefono', e.target.value)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ImpiantoFields({ formData, handleChange }) {
    return (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
                <Label>Potenza Nominale (kWp)</Label>
                <Input type="number" step="0.1" value={formData.potenza_nominale_kwp || ''} onChange={e => handleChange('potenza_nominale_kwp', e.target.value === '' ? null : parseFloat(e.target.value))} />
            </div>

            <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Moduli Fotovoltaici</p>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Numero Moduli</Label>
                        <Input type="number" value={formData.numero_moduli || ''} onChange={e => handleChange('numero_moduli', e.target.value === '' ? null : parseInt(e.target.value))} />
                    </div>
                    <div>
                        <Label>Marca</Label>
                        <Input value={formData.moduli_marca || ''} onChange={e => handleChange('moduli_marca', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                        <Label>Modello</Label>
                        <Input value={formData.moduli_modello || ''} onChange={e => handleChange('moduli_modello', e.target.value)} />
                    </div>
                </div>
            </div>

            <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Inverter</p>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Numero Inverter</Label>
                        <Input type="number" value={formData.numero_inverter || ''} onChange={e => handleChange('numero_inverter', e.target.value === '' ? null : parseInt(e.target.value))} />
                    </div>
                    <div>
                        <Label>Marca</Label>
                        <Input value={formData.inverter_marca || ''} onChange={e => handleChange('inverter_marca', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                        <Label>Modello</Label>
                        <Input value={formData.inverter_modello || ''} onChange={e => handleChange('inverter_modello', e.target.value)} />
                    </div>
                </div>
            </div>

            <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                    <input type="checkbox" id="accumulo" checked={formData.accumulo_presente || false} onChange={e => handleChange('accumulo_presente', e.target.checked)} className="rounded" />
                    <Label htmlFor="accumulo" className="cursor-pointer">Sistema di Accumulo</Label>
                </div>
                {formData.accumulo_presente && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                        <div>
                            <Label>Capacità (kWh)</Label>
                            <Input type="number" step="0.1" value={formData.batteria_capacita_kwh || ''} onChange={e => handleChange('batteria_capacita_kwh', e.target.value === '' ? null : parseFloat(e.target.value))} />
                        </div>
                        <div>
                            <Label>Marca</Label>
                            <Input value={formData.batteria_marca || ''} onChange={e => handleChange('batteria_marca', e.target.value)} />
                        </div>
                        <div className="col-span-2">
                            <Label>Modello</Label>
                            <Input value={formData.batteria_modello || ''} onChange={e => handleChange('batteria_modello', e.target.value)} />
                        </div>
                    </div>
                )}
            </div>

            <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Installazione</p>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Tipo Installazione</Label>
                        <Select value={formData.tipo_installazione || ''} onValueChange={v => handleChange('tipo_installazione', v)}>
                            <SelectTrigger><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="tetto_piano">Tetto Piano</SelectItem>
                                <SelectItem value="tetto_inclinato">Tetto Inclinato</SelectItem>
                                <SelectItem value="pensilina">Pensilina</SelectItem>
                                <SelectItem value="terra">A Terra</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Orientamento</Label>
                        <Select value={formData.orientamento || ''} onValueChange={v => handleChange('orientamento', v)}>
                            <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="nord">Nord</SelectItem>
                                <SelectItem value="sud">Sud</SelectItem>
                                <SelectItem value="est">Est</SelectItem>
                                <SelectItem value="ovest">Ovest</SelectItem>
                                <SelectItem value="sud-est">Sud-Est</SelectItem>
                                <SelectItem value="sud-ovest">Sud-Ovest</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Inclinazione (gradi)</Label>
                        <Input type="number" value={formData.inclinazione_gradi || ''} onChange={e => handleChange('inclinazione_gradi', e.target.value === '' ? null : parseInt(e.target.value))} />
                    </div>
                    <div>
                        <Label>Producibilità Stimata (kWh/anno)</Label>
                        <Input type="number" value={formData.producibilita_stimata_kwh_anno || ''} onChange={e => handleChange('producibilita_stimata_kwh_anno', e.target.value === '' ? null : parseFloat(e.target.value))} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export function EditDialog({ open, onClose, title, section, data, onSave }) {
    const [formData, setFormData] = useState(data || {});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setFormData(data || {});
    }, [data, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(formData);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl" data-testid="base-data-edit-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="base-data-edit-title">{title}</DialogTitle>
                </DialogHeader>
                {section === 'immobile' && <ImmobileFields formData={formData} handleChange={handleChange} />}
                {section === 'impianto' && <ImpiantoFields formData={formData} handleChange={handleChange} />}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="base-data-cancel-btn">
                        <X className="w-4 h-4 mr-2" /> Annulla
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="base-data-save-btn">
                        <FloppyDisk className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
